import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Avatar, Pill, Chip, Spinner, strengthColor, companyStatusColor, stageColor } from "../components/ui-bits";
import { ActionMenu, ConfirmDialog } from "../components/crud";
import { CompanyForm, DealForm, PersonForm } from "../components/record-forms";
import { useToast } from "../hooks/use-toast";
import { Search, Filter, ArrowUpDown, Plus, ExternalLink, SlidersHorizontal } from "lucide-react";

function Toolbar({ type, count, query, setQuery, onNew }) {
  const label = type === "people" ? "person" : type === "companies" ? "company" : "deal";
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[hsl(240,6%,92%)]">
      <div className="flex items-center gap-1.5 rounded-md bg-[hsl(240,5%,96%)] px-2 py-[5px] text-[13px] font-medium">All {count}</div>
      <button className="flex items-center gap-1.5 rounded-md px-2 py-[5px] text-[13px] text-[hsl(240,4%,45%)] hover:bg-[hsl(240,5%,96%)]"><Filter className="h-[15px] w-[15px]" /> Filter</button>
      <button className="flex items-center gap-1.5 rounded-md px-2 py-[5px] text-[13px] text-[hsl(240,4%,45%)] hover:bg-[hsl(240,5%,96%)]"><ArrowUpDown className="h-[15px] w-[15px]" /> Sort</button>
      <button onClick={onNew} className="inline-flex items-center gap-1.5 rounded-md brand-bg text-white px-2.5 py-[6px] text-[13px] font-medium hover:brightness-105">
        <Plus className="h-4 w-4" /> New {label}
      </button>
      <div className="ml-auto flex items-center gap-2">
        <div className="flex items-center gap-1.5 rounded-md border border-[hsl(240,6%,90%)] px-2 py-[5px] w-[200px]">
          <Search className="h-[15px] w-[15px] text-[hsl(240,4%,55%)]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" className="w-full bg-transparent text-[13px] outline-none placeholder:text-[hsl(240,4%,60%)]" />
        </div>
        <button className="p-[6px] rounded-md text-[hsl(240,4%,45%)] hover:bg-[hsl(240,5%,96%)]" aria-label="View settings"><SlidersHorizontal className="h-[15px] w-[15px]" /></button>
      </div>
    </div>
  );
}

function Th({ children, w }) {
  return <th className="text-left px-3 py-2 text-[12px] font-medium text-[hsl(240,4%,48%)] border-r border-[hsl(240,6%,93%)] last:border-r-0 whitespace-nowrap" style={{ minWidth: w }}>{children}</th>;
}
function Td({ children }) {
  return <td className="px-3 py-[7px] text-[13px] border-r border-[hsl(240,6%,94%)] last:border-r-0 align-middle">{children}</td>;
}

export default function RecordsPage({ type }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = () => {
    setLoading(true);
    const fn = type === "people" ? api.people : type === "companies" ? api.companies : api.deals;
    const requests = [fn()];
    if (type !== "companies") requests.push(api.companies());
    Promise.all(requests).then(([records, companyList]) => {
      setRows(records);
      if (companyList) setCompanies(companyList);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const label = type === "people" ? "person" : type === "companies" ? "company" : "deal";
  const openCreate = () => setModal({ mode: "create", row: {} });
  const openEdit = (row) => setModal({ mode: "edit", row });

  const submit = async (values) => {
    const selectedCompany = companies.find((company) => company.id === values.company_id);
    const payload = (type === "people" || type === "deals")
      ? { ...values, company: selectedCompany?.name || "" }
      : values;
    const record = modal.mode === "create"
      ? await (type === "people" ? api.createPerson(payload) : type === "companies" ? api.createCompany(payload) : api.createDeal(payload))
      : await (type === "people" ? api.updatePerson(modal.row.id, payload) : type === "companies" ? api.updateCompany(modal.row.id, payload) : api.updateDeal(modal.row.id, payload));
    if (modal.mode === "create") {
      setRows((current) => [{ ...record, ...(type === "companies" ? { people_count: 0 } : {}) }, ...current]);
      toast({ title: `${label[0].toUpperCase()}${label.slice(1)} created`, description: record.name });
    } else {
      setRows((current) => current.map((item) => item.id === record.id ? { ...item, ...record, ...(type === "companies" ? { people_count: item.people_count } : {}) } : item));
      toast({ title: `${label[0].toUpperCase()}${label.slice(1)} updated`, description: record.name });
    }
    setModal(null);
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      await (type === "people" ? api.deletePerson(deleting.id) : type === "companies" ? api.deleteCompany(deleting.id) : api.deleteDeal(deleting.id));
      setRows((current) => current.filter((item) => item.id !== deleting.id));
      toast({ title: `${label[0].toUpperCase()}${label.slice(1)} deleted`, description: deleting.name });
      setDeleting(null);
    } catch (error) {
      toast({ title: `Could not delete ${label}`, description: error?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) return <Spinner />;
  const filtered = rows.filter((r) => JSON.stringify(r).toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="h-full flex flex-col animate-in-up">
      <Toolbar type={type} count={filtered.length} query={query} setQuery={setQuery} onNew={openCreate} />
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 bg-[hsl(240,20%,99%)] z-10">
            <tr className="border-b border-[hsl(240,6%,92%)]">
              <th className="w-9 border-r border-[hsl(240,6%,93%)]"></th>
              {type === "people" && (<><Th w={200}>Name</Th><Th w={220}>Email</Th><Th w={140}>Company</Th><Th w={160}>Job title</Th><Th w={150}>Location</Th><Th w={130}>Connection</Th></>)}
              {type === "companies" && (<><Th w={180}>Company</Th><Th w={150}>Domain</Th><Th w={140}>Industry</Th><Th w={100}>People</Th><Th w={100}>ARR</Th><Th w={120}>Status</Th></>)}
              {type === "deals" && (<><Th w={240}>Deal</Th><Th w={130}>Company</Th><Th w={130}>Stage</Th><Th w={120}>Value</Th><Th w={140}>Probability</Th><Th w={130}>Close date</Th></>)}
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <tr key={row.id} onClick={() => type === "companies" && navigate(`/companies/${row.id}`)} className={`group border-b border-[hsl(240,6%,94%)] hover:bg-[hsl(240,20%,99%)] transition-colors ${type === "companies" ? "cursor-pointer" : ""}`}>
                <td className="w-9 text-center text-[11px] text-[hsl(240,4%,65%)] border-r border-[hsl(240,6%,94%)] tabular">{i + 1}</td>
                {type === "people" && (<>
                  <Td><button onClick={(e) => { e.stopPropagation(); navigate(`/people/${row.id}`); }} className="flex items-center gap-2 hover:text-[hsl(245,50%,50%)]"><Avatar text={row.avatar} brand /><span className="font-medium">{row.name}</span></button></Td>
                  <Td><span className="text-[hsl(245,50%,50%)]">{row.email}</span></Td>
                  <Td>{row.company_id ? <button onClick={(e) => { e.stopPropagation(); navigate(`/companies/${row.company_id}`); }}><Chip>{row.company}</Chip></button> : <Chip>{row.company}</Chip>}</Td>
                  <Td><span>{row.job_title}</span></Td>
                  <Td><span className="text-[hsl(240,4%,45%)]">{row.location}</span></Td>
                  <Td><Pill className={strengthColor[row.connection_strength] || strengthColor.Weak}>{row.connection_strength}</Pill></Td>
                </>)}
                {type === "companies" && (<>
                  <Td><div className="flex items-center gap-2"><Avatar text={row.logo} /><span className="font-medium">{row.name}</span></div></Td>
                  <Td><span className="inline-flex items-center gap-1 text-[hsl(245,50%,50%)]">{row.domain}<ExternalLink className="h-3 w-3" /></span></Td>
                  <Td><span className="text-[hsl(240,4%,45%)]">{row.industry}</span></Td>
                  <Td><span className="tabular">{row.people_count}</span></Td>
                  <Td><span className="font-medium tabular">{row.arr}</span></Td>
                  <Td><Pill className={companyStatusColor[row.status] || companyStatusColor.Lead}>{row.status}</Pill></Td>
                </>)}
                {type === "deals" && (<>
                  <Td><span className="font-medium">{row.name}</span></Td>
                  <Td>{row.company_id ? <button onClick={(e) => { e.stopPropagation(); navigate(`/companies/${row.company_id}`); }}><Chip>{row.company}</Chip></button> : <Chip>{row.company}</Chip>}</Td>
                  <Td><Pill className={stageColor[row.stage] || stageColor.Discovery}>{row.stage}</Pill></Td>
                  <Td><span className="font-medium tabular">{row.value}</span></Td>
                  <Td><div className="flex items-center gap-2"><div className="h-1.5 w-16 rounded-full bg-[hsl(240,5%,92%)] overflow-hidden"><div className="h-full brand-bg" style={{ width: `${row.probability}%` }} /></div><span className="text-[12px] text-[hsl(240,4%,45%)] tabular">{row.probability}%</span></div></Td>
                  <Td><span className="text-[hsl(240,4%,45%)]">{row.close_date}</span></Td>
                </>)}
                <td className="px-2 text-right"><ActionMenu onEdit={() => openEdit(row)} onDelete={() => setDeleting(row)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-center py-16 text-[13px] text-[hsl(240,4%,55%)]">No {type} found. Create your first {label}.</div>}
      </div>

      {type === "people" && <PersonForm open={!!modal} onClose={() => setModal(null)} onSubmit={submit} initialValues={modal?.row} companies={companies} />}
      {type === "companies" && <CompanyForm open={!!modal} onClose={() => setModal(null)} onSubmit={submit} initialValues={modal?.row} />}
      {type === "deals" && <DealForm open={!!modal} onClose={() => setModal(null)} onSubmit={submit} initialValues={modal?.row} companies={companies} />}
      <ConfirmDialog open={!!deleting} onClose={() => !deleteBusy && setDeleting(null)} onConfirm={confirmDelete} busy={deleteBusy} title={`Delete ${label}?`} description={`“${deleting?.name || "This record"}” will be permanently removed.`} />
    </div>
  );
}
